import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { colored, vary, seededRandom, Fader } from '../../Style.js';
import { SCIFI, metal, glow, halo, hashId, hazardStripes } from './style.js';

// Reactor rings are shared by every pylon and pulse together. They are light,
// not surface, so they never fade and are animated in place.
const CORE_COLOR = new THREE.Color(SCIFI.cyan);
const CORE_MATERIAL = new THREE.MeshBasicMaterial({ color: CORE_COLOR.clone(), toneMapped: false });

// Obstacles keep the exact footprint the server collides against, dressed as
// armoured bulkheads, reactor pylons, cargo containers and lit metal ramps.
export class Obstacle {
  constructor(scene, obstacleData) {
    this.scene = scene;
    this.data = obstacleData;
    this.mesh = null;
    this.fader = null;
    this.spinners = [];
    this.rand = seededRandom(hashId(obstacleData.id));
    this.create();
  }

  create() {
    const { type, position, size, rotation } = this.data;

    this.mesh = new THREE.Group();

    switch (type) {
      case 'wall':
        this.buildWall(size);
        break;
      case 'pillar':
        this.buildPillar(size);
        break;
      case 'cover':
        this.buildCover(size);
        break;
      case 'ramp':
        // Zero elevations are omitted from the server's JSON
        this.buildRamp(size, this.data.elevationStart ?? 0, this.data.elevationEnd ?? 0);
        break;
      default:
        this.addMerged([colored(new THREE.BoxGeometry(size.x, size.y, size.z), SCIFI.alloy, { y: size.y / 2 })]);
    }

    // Ramps are built from their footprint origin; everything else from its base
    this.mesh.position.set(position.x, position.y, position.z);

    if (rotation) {
      this.mesh.rotation.y = rotation;
    }

    this.scene.add(this.mesh);
    this.mesh.updateMatrixWorld(true);

    // Ramps are walked on, so they never fade out of the way
    if (type !== 'ramp') {
      this.fader = new Fader(this.mesh);
    }
  }

  addMerged(geometries, material = metal(0xffffff, { vertexColors: true })) {
    const mesh = new THREE.Mesh(mergeGeometries(geometries), material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.mesh.add(mesh);
    return mesh;
  }

  // Emissive strips: unlit, vertex coloured and pushed a little past white.
  addLights(geometries) {
    const mesh = new THREE.Mesh(mergeGeometries(geometries), glow(0xffffff, { vertexColors: true, boost: 1.6 }));
    this.mesh.add(mesh);
    return mesh;
  }

  // Armoured bulkhead: ribbed panels on both faces, a heavy cap and skirt,
  // and a light strip running the length of each face.
  buildWall(size) {
    const rand = this.rand;
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const thickness = alongX ? size.z : size.x;
    const height = size.y;
    const ry = alongX ? 0 : Math.PI / 2;
    const parts = [];
    const lights = [];

    // Lay everything out along X, then turn it to the wall's axis
    const add = (list, geo, color, x, y, z) => list.push(colored(geo, color, { x, y, z }).rotateY(ry));

    add(parts, new THREE.BoxGeometry(length, 0.6, thickness + 0.35), SCIFI.alloyDark, 0, 0.3, 0);
    add(parts, new THREE.BoxGeometry(length - 0.1, height - 1.0, thickness), SCIFI.alloyDeep, 0, 0.6 + (height - 1.0) / 2, 0);
    add(parts, new THREE.BoxGeometry(length, 0.45, thickness + 0.25), SCIFI.alloyLight, 0, height - 0.22, 0);
    add(parts, new THREE.BoxGeometry(length - 0.3, 0.12, thickness * 0.5), SCIFI.alloyDark, 0, height + 0.05, 0);

    const segments = Math.max(1, Math.round(length / 3.4));
    const segment = length / segments;
    const panelHeight = (height - 1.6) / 2;
    for (let i = 0; i < segments; i++) {
      const cx = -length / 2 + segment * (i + 0.5);
      for (const side of [-1, 1]) {
        const face = side * (thickness / 2 + 0.05);
        // Two stacked plates per bay, slightly different tones
        add(parts, new THREE.BoxGeometry(segment - 0.35, panelHeight - 0.12, 0.1), vary(SCIFI.alloy, rand, 0.08, 0.01), cx, 0.75 + panelHeight / 2, face);
        add(parts, new THREE.BoxGeometry(segment - 0.35, panelHeight - 0.12, 0.1), vary(SCIFI.panel, rand, 0.08, 0.01), cx, 0.75 + panelHeight * 1.5 + 0.1, face);
        // Small vent or status light on some bays
        if (rand() < 0.3) {
          add(parts, new THREE.BoxGeometry(segment * 0.4, 0.2, 0.06), SCIFI.alloyDark, cx, 0.75 + panelHeight * 1.5, face + side * 0.07);
        }
        if (rand() < 0.18) {
          add(lights, new THREE.BoxGeometry(0.18, 0.18, 0.06), SCIFI.amber, cx + segment * 0.35, height - 0.75, face + side * 0.06);
        }
      }
      // Structural rib between bays
      if (i > 0) {
        add(parts, new THREE.BoxGeometry(0.32, height - 0.5, thickness + 0.3), SCIFI.alloyDark, -length / 2 + segment * i, (height - 0.5) / 2 + 0.1, 0);
      }
    }
    // End caps
    for (const end of [-1, 1]) {
      add(parts, new THREE.BoxGeometry(0.5, height, thickness + 0.4), SCIFI.alloyDark, end * (length / 2 - 0.25), height / 2, 0);
    }

    // Light strips along both faces, just under the cap
    for (const side of [-1, 1]) {
      add(lights, new THREE.BoxGeometry(length - 1.2, 0.12, 0.06), SCIFI.cyan, 0, height - 0.6, side * (thickness / 2 + 0.11));
      add(lights, new THREE.BoxGeometry(length - 1.2, 0.06, 0.06), SCIFI.cyanSoft, 0, 0.66, side * (thickness / 2 + 0.2));
    }

    this.addMerged(parts);
    this.addLights(lights);
  }

  // Reactor pylon: finned column on a heavy plinth with glowing core rings.
  buildPillar(size) {
    const radius = size.x / 2;
    const height = size.y;
    const parts = [];
    const lights = [];

    parts.push(colored(new THREE.CylinderGeometry(radius * 1.25, radius * 1.4, 0.55, 8), SCIFI.alloyDark, { y: 0.27, ry: Math.PI / 8 }));
    parts.push(colored(new THREE.CylinderGeometry(radius * 0.7, radius * 0.8, height - 0.9, 8), SCIFI.alloyDeep, { y: 0.5 + (height - 0.9) / 2, ry: Math.PI / 8 }));
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const fin = new THREE.BoxGeometry(radius * 0.38, height - 1.5, 0.28);
      parts.push(colored(fin, SCIFI.alloy, { x: radius * 0.78, y: 0.55 + (height - 1.5) / 2 }).rotateY(a));
      // A light slit down each gap between fins
      lights.push(colored(new THREE.BoxGeometry(0.06, height - 2.4, 0.12), SCIFI.cyanSoft, { x: radius * 0.72, y: 1.0 + (height - 2.4) / 2 }).rotateY(a + Math.PI / 4));
    }
    for (const y of [height * 0.5 - 0.6, height - 0.75]) {
      parts.push(colored(new THREE.CylinderGeometry(radius * 1.02, radius * 1.02, 0.3, 8), SCIFI.alloyLight, { y, ry: Math.PI / 8 }));
    }
    parts.push(colored(new THREE.CylinderGeometry(radius * 0.75, radius * 1.05, 0.35, 8), SCIFI.alloyDark, { y: height - 0.42, ry: Math.PI / 8 }));
    parts.push(colored(new THREE.CylinderGeometry(radius * 0.4, radius * 0.5, 0.25, 8), SCIFI.alloy, { y: height - 0.12 }));

    this.addMerged(parts);
    this.addLights(lights);

    // Two glowing core rings, open cylinders hugging the column
    for (const y of [height * 0.28, height * 0.5 + 0.05]) {
      const ring = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.84, radius * 0.84, 0.28, 16, 1, true), CORE_MATERIAL);
      ring.position.y = y;
      ring.userData.noFade = true;
      this.mesh.add(ring);
    }

    // A small energy node spinning above the cap
    const node = new THREE.Mesh(new THREE.OctahedronGeometry(radius * 0.28, 0), CORE_MATERIAL);
    node.position.y = height + radius * 0.45;
    node.userData.baseY = node.position.y;
    node.userData.noFade = true;
    this.mesh.add(node);
    this.spinners.push(node);
    const glowSprite = halo(SCIFI.cyan, radius * 2.2, 0.55);
    glowSprite.position.y = node.position.y;
    glowSprite.userData.noFade = true;
    this.mesh.add(glowSprite);
  }

  // Thin barriers become armoured barricades; squat blocks become cargo.
  buildCover(size) {
    const longest = Math.max(size.x, size.z);
    const shortest = Math.min(size.x, size.z);
    if (longest / shortest > 3) {
      this.buildBarricade(size);
    } else {
      this.buildCargo(size);
    }
  }

  buildBarricade(size) {
    const rand = this.rand;
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const thickness = alongX ? size.z : size.x;
    const height = size.y;
    const ry = alongX ? 0 : Math.PI / 2;
    const parts = [];
    const lights = [];
    const add = (list, geo, color, x, y, z) => list.push(colored(geo, color, { x, y, z }).rotateY(ry));

    add(parts, new THREE.BoxGeometry(length, height * 0.75, thickness * 0.7), SCIFI.alloyDeep, 0, height * 0.375, 0);
    // Sloped armour plates on both faces
    const plates = Math.max(2, Math.round(length / 2.2));
    const plate = length / plates;
    for (let i = 0; i < plates; i++) {
      const cx = -length / 2 + plate * (i + 0.5);
      for (const side of [-1, 1]) {
        const geo = new THREE.BoxGeometry(plate - 0.12, height * 0.8, 0.14);
        geo.rotateX(side * 0.12);
        add(parts, geo, vary(SCIFI.alloy, rand, 0.08), cx, height * 0.42, side * thickness * 0.38);
      }
    }
    add(parts, new THREE.BoxGeometry(length, 0.22, thickness), SCIFI.alloyLight, 0, height - 0.11, 0);
    parts.push(...hazardStripes(length, thickness + 0.02, 0.1, { y: height * 0.82, ry }));
    for (const side of [-1, 1]) {
      add(lights, new THREE.BoxGeometry(length - 0.4, 0.08, 0.05), SCIFI.cyan, 0, 0.3, side * (thickness * 0.45 + 0.08));
    }
    this.addMerged(parts);
    this.addLights(lights);
  }

  // A ribbed cargo container filling the footprint with a tech crate on top.
  buildCargo(size) {
    const rand = this.rand;
    const parts = [];
    const lights = [];
    const baseHeight = size.y * 0.66;
    const alongX = size.x >= size.z;
    const body = SCIFI.containers[Math.floor(rand() * SCIFI.containers.length)];

    parts.push(colored(new THREE.BoxGeometry(size.x - 0.2, baseHeight - 0.2, size.z - 0.2), body, { y: baseHeight / 2 }));
    // Corrugation ribs on all four faces
    const ribs = (span, depth, axisX) => {
      const count = Math.max(3, Math.round(span / 0.4));
      for (let i = 1; i < count; i++) {
        const t = -span / 2 + (span / count) * i;
        for (const side of [-1, 1]) {
          const geo = new THREE.BoxGeometry(axisX ? 0.14 : 0.1, baseHeight - 0.5, axisX ? 0.1 : 0.14);
          parts.push(colored(geo, vary(body, rand, 0.06), {
            x: axisX ? t : side * (depth / 2 - 0.08),
            z: axisX ? side * (depth / 2 - 0.08) : t,
            y: baseHeight / 2
          }));
        }
      }
    };
    ribs(size.x - 0.6, size.z, true);
    ribs(size.z - 0.6, size.x, false);
    // Dark corner frame
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        parts.push(colored(new THREE.BoxGeometry(0.28, baseHeight, 0.28), SCIFI.alloyDark, { x: sx * (size.x / 2 - 0.14), y: baseHeight / 2, z: sz * (size.z / 2 - 0.14) }));
      }
    }
    for (const y of [0.1, baseHeight - 0.1]) {
      parts.push(colored(new THREE.BoxGeometry(size.x, 0.2, size.z), SCIFI.alloyDark, { y }));
    }
    // Stencilled ID stripe in white
    parts.push(colored(new THREE.BoxGeometry(alongX ? size.x * 0.3 : 0.04, 0.3, alongX ? 0.04 : size.z * 0.3), SCIFI.trim, {
      x: alongX ? -size.x * 0.2 : size.x / 2 + 0.01, y: baseHeight * 0.7, z: alongX ? size.z / 2 + 0.01 : -size.z * 0.2
    }));

    // Tech crate stacked off-centre, with a glowing seam
    const topHeight = size.y - baseHeight;
    const cw = size.x * 0.5;
    const cd = size.z * 0.5;
    const cx = -size.x * 0.18;
    const cz = -size.z * 0.15;
    const turn = (rand() - 0.5) * 0.4;
    parts.push(colored(new THREE.BoxGeometry(cw, topHeight - 0.05, cd), SCIFI.alloyLight, { x: cx, y: baseHeight + topHeight / 2, z: cz, ry: turn }));
    parts.push(colored(new THREE.BoxGeometry(cw + 0.06, 0.14, cd + 0.06), SCIFI.alloyDark, { x: cx, y: baseHeight + 0.1, z: cz, ry: turn }));
    parts.push(colored(new THREE.BoxGeometry(cw + 0.06, 0.14, cd + 0.06), SCIFI.alloyDark, { x: cx, y: baseHeight + topHeight - 0.12, z: cz, ry: turn }));
    lights.push(colored(new THREE.BoxGeometry(cw + 0.08, 0.06, cd + 0.08), SCIFI.cyan, { x: cx, y: baseHeight + topHeight * 0.55, z: cz, ry: turn }));

    // A small canister beside it and a status lamp on the container
    const canister = Math.min(size.x, size.z) * 0.14;
    parts.push(colored(new THREE.CylinderGeometry(canister, canister, topHeight * 0.8, 10), SCIFI.alloy, { x: size.x * 0.28, y: baseHeight + topHeight * 0.4, z: size.z * 0.25 }));
    lights.push(colored(new THREE.CylinderGeometry(canister * 1.02, canister * 1.02, 0.1, 10), SCIFI.amber, { x: size.x * 0.28, y: baseHeight + topHeight * 0.6, z: size.z * 0.25 }));
    lights.push(colored(new THREE.BoxGeometry(0.2, 0.2, 0.2), rand() < 0.5 ? SCIFI.heal : SCIFI.amber, { x: size.x / 2 - 0.3, y: baseHeight - 0.35, z: size.z / 2 - 0.05 }));

    this.addMerged(parts);
    this.addLights(lights);
  }

  // Metal ramp or raised platform: plated deck, hazard stripes at the foot,
  // and light strips along the edges.
  buildRamp(size, elevStart, elevEnd) {
    const body = new THREE.Mesh(this.createRampGeometry(size, elevStart, elevEnd), metal(SCIFI.alloyDark));
    body.castShadow = true;
    body.receiveShadow = true;
    this.mesh.add(body);

    const rise = elevEnd - elevStart;
    const slope = Math.atan2(rise, size.z);
    const slopeLength = Math.hypot(size.z, rise);
    const midY = (elevStart + elevEnd) / 2;
    const flat = Math.abs(rise) < 0.01;
    const parts = [];
    const lights = [];

    // Deck plates (kept very thin so things standing on the deck sit on top)
    const plates = Math.max(2, Math.round(slopeLength / 2.5));
    const plateDepth = slopeLength / plates;
    for (let i = 0; i < plates; i++) {
      const t = (i + 0.5) / plates;
      parts.push(colored(new THREE.BoxGeometry(size.x - 1.2, 0.04, plateDepth - 0.12), vary(SCIFI.alloy, this.rand, 0.08), {
        y: elevStart + t * rise + 0.02, z: -size.z / 2 + t * size.z, rx: -slope
      }));
    }

    // Raised kerbs along both sides with light strips on their inner edge
    for (const side of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.5, 0.25, slopeLength), SCIFI.alloyLight, {
        x: side * (size.x / 2 - 0.25), y: midY + 0.12, rx: -slope
      }));
      lights.push(colored(new THREE.BoxGeometry(0.1, 0.06, slopeLength - 0.4), SCIFI.cyan, {
        x: side * (size.x / 2 - 0.55), y: midY + 0.03, rx: -slope
      }));
      // Vertical ribs along the side walls
      const ribs = Math.max(2, Math.round(size.z / 2.5));
      for (let i = 0; i <= ribs; i++) {
        const t = i / ribs;
        const h = elevStart + t * rise;
        if (h < 0.3) continue;
        parts.push(colored(new THREE.BoxGeometry(0.12, h, 0.3), SCIFI.alloy, {
          x: side * (size.x / 2 + 0.06), y: h / 2, z: -size.z / 2 + t * size.z
        }));
      }
    }

    if (flat) {
      // Platform: lit edges front and back too, and a landing circle on the deck
      for (const end of [-1, 1]) {
        parts.push(colored(new THREE.BoxGeometry(size.x, 0.25, 0.5), SCIFI.alloyLight, { y: elevStart + 0.12, z: end * (size.z / 2 - 0.25) }));
        lights.push(colored(new THREE.BoxGeometry(size.x - 1.1, 0.06, 0.1), SCIFI.cyan, { y: elevStart + 0.03, z: end * (size.z / 2 - 0.55) }));
        for (let i = 0; i <= 6; i++) {
          const x = -size.x / 2 + (size.x / 6) * i;
          parts.push(colored(new THREE.BoxGeometry(0.3, elevStart, 0.12), SCIFI.alloy, { x, y: elevStart / 2, z: end * (size.z / 2 + 0.06) }));
        }
      }
      const r = Math.min(size.x, size.z) * 0.3;
      parts.push(colored(new THREE.RingGeometry(r, r + 0.25, 40), SCIFI.trim, { rx: -Math.PI / 2, y: elevStart + 0.045 }));
      parts.push(colored(new THREE.RingGeometry(r + 0.6, r + 0.7, 40), SCIFI.hazard, { rx: -Math.PI / 2, y: elevStart + 0.045 }));
    } else {
      // Hazard stripes across the foot and the crest of the ramp
      const lowEnd = elevStart < elevEnd ? -1 : 1;
      const lowY = Math.min(elevStart, elevEnd);
      parts.push(...hazardStripes(size.x - 1.0, 0.9, 0.06, { y: lowY + 0.04 + Math.abs(rise) * 0.04, z: lowEnd * (size.z / 2 - 0.5), stripe: 0.7 }));
      // Lights along the foot so the ramp entrance is easy to spot
      lights.push(colored(new THREE.BoxGeometry(size.x - 1.0, 0.06, 0.12), SCIFI.amber, { y: lowY + 0.04, z: lowEnd * (size.z / 2 - 0.06) }));
      // Chevrons on the deck pointing up the slope
      const up = -lowEnd;
      for (let i = 0; i < 3; i++) {
        const t = 0.35 + i * 0.18;
        const z = -size.z / 2 + (lowEnd < 0 ? t : 1 - t) * size.z;
        for (const side of [-1, 1]) {
          const arm = colored(new THREE.BoxGeometry(1.15, 0.03, 0.22), SCIFI.cyanSoft);
          arm.rotateY(Math.atan2(up * 0.7, side * 0.9));
          arm.translate(side * 0.45, 0, z - up * 0.35);
          // Tilt into the plane of the deck around the ramp's centre
          arm.rotateX(-slope);
          arm.translate(0, midY + 0.06, 0);
          lights.push(arm);
        }
      }
    }

    // Ramps never fade, so their meshes can share materials
    const deck = new THREE.Mesh(mergeGeometries(parts), metal(0xffffff, { vertexColors: true }));
    deck.castShadow = true;
    deck.receiveShadow = true;
    this.mesh.add(deck);
    this.addLights(lights);
  }

  createRampGeometry(size, elevStart, elevEnd) {
    // The ramp goes from elevStart at -Z to elevEnd at +Z
    const halfX = size.x / 2;
    const halfZ = size.z / 2;

    const vertices = new Float32Array([
      // Bottom face (y = 0)
      -halfX, 0, -halfZ,
       halfX, 0, -halfZ,
       halfX, 0,  halfZ,
      -halfX, 0,  halfZ,

      // Top face (sloped)
      -halfX, elevStart, -halfZ,
       halfX, elevStart, -halfZ,
       halfX, elevEnd,    halfZ,
      -halfX, elevEnd,    halfZ,
    ]);

    const indices = [
      // Bottom
      0, 1, 2,  0, 2, 3,
      // Top (sloped surface)
      4, 6, 5,  4, 7, 6,
      // Front (low end)
      0, 4, 5,  0, 5, 1,
      // Back (high end)
      2, 6, 7,  2, 7, 3,
      // Left side
      0, 3, 7,  0, 7, 4,
      // Right side
      1, 5, 6,  1, 6, 2,
    ];

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    // Split into flat faces so the hard edges shade crisply
    const flat = geometry.toNonIndexed();
    flat.computeVertexNormals();
    return flat;
  }

  // Fade towards the given opacity (1 = solid) when blocking the camera's view.
  setFadeTarget(opacity) {
    if (this.fader) this.fader.target = opacity;
  }

  update(deltaTime, elapsed) {
    if (this.fader) this.fader.update(deltaTime);
    if (this.spinners.length) {
      const pulse = 0.85 + Math.sin(elapsed * 2.2) * 0.25;
      CORE_MATERIAL.color.copy(CORE_COLOR).multiplyScalar(pulse * 1.5);
      for (const spinner of this.spinners) {
        spinner.rotation.y = elapsed * 1.6;
        spinner.position.y = spinner.userData.baseY + Math.sin(elapsed * 1.4) * 0.12;
      }
    }
  }

  remove() {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.traverse(child => {
        if (child.isMesh) child.geometry.dispose();
        if (child.isSprite) child.material.dispose();
      });
      if (this.fader) this.fader.dispose();
      this.mesh = null;
    }
  }
}
